'use es6';

import { jsx as _jsx } from "react/jsx-runtime";
import I18n from 'I18n';
import { toMoment } from 'UIComponents/core/SimpleDate';
import { AudienceTypes, StatusMap, Networks, FBCampaignObjective, EntityTypes, DisabledNetworkMappings } from 'AdsUI/lib/Constants';
import { formatPercentage } from 'AdsUI/lib/Calculations';
import isValidI18nKey from 'I18n/utils/isValidI18nKey';
import { ScheduleTypes } from 'AdsUI/records/ad-management/ScheduleRecord';
import { stageLabelTranslator } from 'property-translator/propertyTranslator';
import { getDateRangePresetForString, PRESET_TYPES } from '../lib/AdsSimpleDateRange';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedDateTime from 'I18n/components/FormattedDateTime';
const ITEMS_PER_PAGE = 10;
export const getCountryName = countryCode => {
  const sharedString = `SharedI18nStrings.countryCodes.${countryCode}`;
  const creationTargetingString = `creation.targeting.additionalCountries.${countryCode}`;

  if (isValidI18nKey(sharedString)) {
    // SharedI18nStrings only has countries that have an official ISO code;
    return I18n.text(sharedString);
  }

  return I18n.text(isValidI18nKey(creationTargetingString) ? creationTargetingString : 'notApplicableAbbreviation');
};
export const formatAdWordsAccountId = accountId => {
  // Format like AdWords as "123-456-7890"
  const idString = accountId.toString();
  return `${idString.substring(0, 3)}-${idString.substring(3, 6)}-${idString.substring(6)}`;
};
export const getDurationInDays = ({
  startDate,
  startTime,
  endDate,
  endTime,
  endDateType,
  dayMax
}) => {
  if (endDateType === ScheduleTypes.DAY_MAX) {
    return dayMax;
  }

  return Math.round(toMoment(endDate).add(endTime, 'minutes').diff(toMoment(startDate).add(startTime, 'minutes'), 'days', true));
};
export const isTrackingEnabled = (campaign, adAccounts) => {
  const {
    entity: {
      network,
      accountId,
      type
    }
  } = campaign;

  if (type === FBCampaignObjective.LEAD_GENERATION) {
    // Lead generation ads are tracked by the networks and forwarded to us, so
    // we don't need to having auto tracking enabled to track them.
    return true;
  } else {
    return adAccounts.find(account => account.network === network && account.accountId === accountId).trackingEnabled;
  }
};
export const isEntityDeleted = (network, status) => {
  return DisabledNetworkMappings[network].includes(status);
};
export const getToggledEntityStatus = (status, network) => {
  switch (network) {
    case Networks.ADWORDS:
      return status === StatusMap.ENABLED ? StatusMap.PAUSED : StatusMap.ENABLED;

    case Networks.FACEBOOK:
    case Networks.LINKEDIN:
      return status === StatusMap.ACTIVE || status === StatusMap.ENABLED ? StatusMap.PAUSED : StatusMap.ACTIVE;

    default:
      return null;
  }
};
export const getAccountNameForCampaign = (campaign, adAccounts) => {
  const {
    network,
    accountId
  } = campaign.entity;
  return adAccounts.find(account => account.network === network && account.accountId === accountId).getDisplayName();
};
export const getCurrencySymbol = currencyCode => {
  if (currencyCode) {
    return I18n.currencySymbols[currencyCode].symbol;
  } else {
    return '';
  }
};
export const getBiglyticsTimeRange = () => {
  return getDateRangePresetForString(PRESET_TYPES.ALL);
}; // Returns a string with the source of the parent audience.

export const getParentAudienceSource = (audience, adAccounts, pixels) => {
  switch (audience.type) {
    case AudienceTypes.CONTACT_LIST:
      {
        return audience.objectListInfo.name;
      }

    case AudienceTypes.WEBSITE:
      {
        const {
          targetingAudience: {
            adNetwork,
            adAccountId,
            pixelId
          }
        } = audience;

        if (adNetwork === Networks.FACEBOOK) {
          const associatedPixel = pixels.find(pixel => pixel.adNetwork === adNetwork && pixel.id === pixelId);
          return associatedPixel ? associatedPixel.name : '--';
        } else {
          const adAccount = adAccounts.find(account => account.network === adNetwork && account.accountId === adAccountId);
          return I18n.text('audiencePage.tagName', {
            accountId: adAccount.getDisplayName()
          });
        }
      }

    default:
      {
        return '--';
      }
  }
};
export const isAudienceNameUnique = (name, audiences) => {
  return audiences.reduce((prev, audience) => prev && audience.networkState.name !== name, true);
};
export const getReportingTimeRangeQuery = ({
  startDate,
  endDate
}) => {
  return {
    'date-from': toMoment(startDate).valueOf(),
    'date-to': toMoment(endDate).valueOf()
  };
};
export const displayDecimalAsPercentage = decimal => {
  if (typeof decimal === 'number') {
    return formatPercentage(decimal * 100);
  } else {
    return '';
  }
};

const UIFormattedDateTime = ({
  value
}) => /*#__PURE__*/_jsx(FormattedDateTime, {
  value: value,
  format: "l",
  type: "userTz"
});

export const getMinutesSinceMidnight = date => {
  // Value of UITimePicker is a number whose value represents minutes
  // since midnight, with a default interval of 30 minutes.
  const minutesSinceMidnight = date.diff(date.clone().startOf('day'), 'minutes'); // Round up to next 30 minute interval

  return Math.ceil(minutesSinceMidnight / 30) * 30;
};
export const convertTimeRangeToMessage = timeRange => {
  const {
    type,
    startDate,
    endDate
  } = timeRange;

  if (type !== PRESET_TYPES.CUSTOM) {
    return I18n.text(`rangeTypes.${type}`);
  }

  const start = toMoment(startDate).valueOf();
  const end = toMoment(endDate).valueOf();
  return /*#__PURE__*/_jsx(FormattedJSXMessage, {
    message: "timeRange_jsx",
    options: {
      start,
      end
    },
    elements: {
      UIFormattedDateTime
    }
  });
};
export const convertTimeRangeToKey = timeRange => {
  const start = toMoment(timeRange.startDate).format('l');
  const end = toMoment(timeRange.endDate).format('l');
  return `${start} - ${end}`;
};
export const getPaginatedData = (sortedAndFilteredData, page) => {
  if (!sortedAndFilteredData) {
    return [null, 1];
  }

  const firstItem = (page - 1) * ITEMS_PER_PAGE;
  const isLastPage = Math.ceil(sortedAndFilteredData.size / ITEMS_PER_PAGE) === page;
  const lastItem = !isLastPage ? page * ITEMS_PER_PAGE : sortedAndFilteredData.size;
  const campaigns = sortedAndFilteredData.slice(firstItem, lastItem);
  const pageCount = Math.ceil(sortedAndFilteredData.size / ITEMS_PER_PAGE) || 1;
  return [campaigns, pageCount];
};
export const lifecycleStageTranslator = (stageId, label) => {
  const translatedStage = stageLabelTranslator({
    label,
    objectType: 'CONTACT',
    pipelineId: 'contacts-lifecycle-pipeline',
    // https://hubspot.slack.com/archives/C01FU5BG3RC/p1624549231219500?thread_ts=1622659434.071300&cid=C01FU5BG3RC
    stageId
  });
  return translatedStage;
}; // Creates a throttled version of a function that will only be called once
// within a certain amount of time.
// From conversations-visitor/conversations-embed/static/js/utils/throttle.js

export const throttle = (func, timeout) => {
  let throttled = false;
  let trailingCall = null;
  return (...args) => {
    if (throttled) {
      trailingCall = () => {
        func(...args);
      };

      return;
    } else {
      throttled = true;
      func(...args);
      setTimeout(() => {
        throttled = false;

        if (typeof trailingCall === 'function') {
          trailingCall();
        }

        trailingCall = null;
      }, timeout);
    }
  };
}; // Replacement for _.omit.
// From conversations-internal-pub-sub/static/js/utils/omit.js

export const omit = (object, toRemove = []) => {
  if (!Array.isArray(toRemove)) toRemove = [toRemove];
  return Object.keys(object).reduce((acc, key) => {
    if (toRemove.includes(key)) return acc;
    acc[key] = object[key];
    return acc;
  }, {});
};
const NETWORK_ENTITY_TYPE_KEY = {
  [Networks.FACEBOOK]: campaignEntity => `facebookGlobal.campaignObjective.${campaignEntity.type}`,
  [Networks.ADWORDS]: campaignEntity => `adWordsGlobal.campaignType.${campaignEntity.type}`,
  [Networks.LINKEDIN]: (__campaignEntity, adGroupEntity) => `linkedInGlobal.campaignObjective.${adGroupEntity.objective}`
};
export const getTypeForEntity = (entity, entityType, campaignEntity) => {
  if (entityType === EntityTypes.SEQUENCE) {
    return I18n.text('campaigns.adSequence');
  }

  if (entityType === EntityTypes.ADGROUP && campaignEntity) {
    return I18n.text(NETWORK_ENTITY_TYPE_KEY[entity.network](campaignEntity, entity));
  }

  if (entityType === EntityTypes.CAMPAIGN) {
    return campaignEntity && campaignEntity.getFormattedType && campaignEntity.getFormattedType();
  }

  return null;
};
export const getCurrencyString = currencyCode => {
  const {
    code,
    symbol
  } = I18n.currencySymbols[currencyCode];

  if (code === symbol) {
    return code;
  }

  return `${code} ${symbol}`;
};
export const getNameFromCurrencySymbol = symbol => I18n.currencySymbols[symbol] ? I18n.currencySymbols[symbol].name : symbol;
export const getDomainFromWebsiteUrl = websiteUrl => {
  if (!websiteUrl) {
    return null;
  }

  return (websiteUrl.split('//')[1] || websiteUrl).split('/')[0].split('?')[0].replace('www.', '');
};
export const getAutomationUrl = ({
  contactOpts,
  portalId
}) => {
  switch (contactOpts.entityType) {
    case EntityTypes.AD:
      return `/ads/${portalId}/${contactOpts.network}/${contactOpts.accountId}/campaigns/${contactOpts.campaignId}/adgroups/${contactOpts.adGroupId}/ad/${contactOpts.adId}/edit/automation`;

    case EntityTypes.ADGROUP:
      return `/ads/${portalId}/${contactOpts.network}/${contactOpts.accountId}/campaigns/${contactOpts.campaignId}/adgroups/${contactOpts.adGroupId}/edit/automation`;

    default:
      return `/ads/${portalId}/${contactOpts.network}/${contactOpts.accountId}/campaigns/${contactOpts.campaignId}/edit/automation`;
  }
};
export const convertAdsEntityTypeToCRMEntityType = entityType => {
  // EntityTypes.CAMPAIGN returns 'CAMPAIGNS' plural, which is used locally but 'CAMPAIGN' singular is generally used outside our app
  return entityType === EntityTypes.CAMPAIGN ? 'CAMPAIGN' : entityType.toLocaleUpperCase();
};